import { useCallback, useMemo, useState } from "react"
import { useNavigate } from "react-router"
import { enqueueSnackbar } from "notistack"
import { Grid, MenuItem } from "@mui/material"
import { MRT_ColumnDef } from "material-react-table"
import { deleteSalesAccount, fetchSalesAccounts, SalesAccountsQuery, SalesAccountsResponse } from "../salesAccountsApi"
import { DeleteConfirmDialog } from "../../../../../shared/components/dialogs/DeleteConfirmDialog"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import { StandardButton } from "../../../../../shared/components/buttons/StandardButton"
import { PageHeader } from "../../../../../shared/components/pageHeader/PageHeader"
import { useApiQueryTable } from "../../../../../core/api/components/ApiQueryTable"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { Page } from "../../../../../shared/components/pageHeader/PageHeaderApi"
import { useApiMutation } from "../../../../../core/api/useApiMutation"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../shared/components/links/StandardBreadcrumbs"
import { useMerchant } from "../../../../merchants/merchantHooks"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import {
    MERCHANT_ADMIN_ROUTE,
    getEditSalesAccountRout,
    MERCHANT_ADMIN_SETTINGS_SALES_ACCOUNT_CREATE_ROUTE,
} from "../../../../../constants/routes"
import { SalesAccount } from "../salesAccountsTypes"
import { DestructiveMenuItem } from "../../../../../shared/components/menus/DestructiveMenuItem"

const defaultModalState = {
    showConfirmModal: false,
    confirmModalText: "",
    itemId: null,
}
const selectSalesAccounts = (data: SalesAccountsResponse) => data?.salesAccounts || []

export function SalesAccounts() {
    const { merchantId } = useMerchant()
    const { l10n } = useAppLocalization()
    const navigate = useNavigate()

    const [confirmModalState, setConfirmModalState] = useState<{
        showConfirmModal: boolean
        confirmModalText: string
        itemId: null | number
    }>(defaultModalState)

    const salesAccountsResult = useApiQuery(fetchSalesAccounts, {
        queryName: SalesAccountsQuery.LIST,
        dependencies: {
            merchantId,
        },
        select: selectSalesAccounts,
    })

    const { ApiQueryTable, columnFactory } = useApiQueryTable(salesAccountsResult)

    const columns = useMemo<MRT_ColumnDef<SalesAccount>[]>(
        () => [
            columnFactory.nameColumn({
                header: l10n.getString("sales-accounts-table-name-column"),
                accessorKey: "name",
            }),
            columnFactory.shortNameColumn({
                header: l10n.getString("sales-accounts-table-number-column"),
                accessorKey: "number",
            }),
            columnFactory.activeInactiveStatusColumn(
                {
                    header: l10n.getString("status"),
                },
                (row) => row.isActive,
            ),
        ],
        [columnFactory, l10n],
    )

    const { mutate: callDeleteSalesAccount } = useApiMutation(deleteSalesAccount, {
        async onSuccess(_, data, context) {
            await context.queryClient.invalidateQueries({ queryKey: [SalesAccountsQuery.LIST] })
            enqueueSnackbar({
                variant: "success",
                message: data.serviceResponse.displayMessage,
            })
        },
    })

    const handleDeleteClick = useCallback(() => {
        if (confirmModalState.itemId != null) {
            callDeleteSalesAccount({ salesAccountId: confirmModalState.itemId })
        } else {
            throw new Error("Sales account id is undefined")
        }
    }, [confirmModalState.itemId, callDeleteSalesAccount])

    return (
        <>
            <LocalizedStrict id="sales-accounts-page-header" attrs={{ title: true }}>
                <PageHeader
                    title="Sales accounts"
                    page={Page.MERCHANT_ADMIN_SETTINGS_SALES_ACCOUNTS}
                    rightElement={
                        <LocalizedStrict id="sales-accounts-table-create-button">
                            <StandardButton
                                onClick={() => navigate(MERCHANT_ADMIN_SETTINGS_SALES_ACCOUNT_CREATE_ROUTE)}
                            >
                                Create new
                            </StandardButton>
                        </LocalizedStrict>
                    }
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="merchant-nav-item-home">
                            <BreadcrumbLink to={MERCHANT_ADMIN_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="merchant-nav-item-settings-header">
                            <Breadcrumb>Settings</Breadcrumb>
                        </LocalizedStrict>
                        <LocalizedStrict id="merchant-nav-item-sales-accounts">
                            <Breadcrumb>Sales accounts</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>

            <Grid container direction="column">
                <ApiQueryTable
                    columns={columns}
                    initialState={{ density: "spacious" }}
                    renderRowActionMenuItems={({ row, closeMenu }) => [
                        <LocalizedStrict id="sales-accounts-table-menu-item-edit">
                            <MenuItem
                                onClick={() => {
                                    navigate(getEditSalesAccountRout(row.original.id))
                                }}
                                key={`edit-${row.original.id}`}
                            >
                                Edit
                            </MenuItem>
                        </LocalizedStrict>,
                        <LocalizedStrict id="sales-accounts-table-menu-item-delete">
                            <DestructiveMenuItem
                                onClick={() => {
                                    setConfirmModalState({
                                        showConfirmModal: true,
                                        confirmModalText: `${row.original.name}`,
                                        itemId: row.original.id,
                                    })
                                    closeMenu()
                                }}
                                key={`delete-${row.original.id}`}
                            >
                                Delete
                            </DestructiveMenuItem>
                        </LocalizedStrict>,
                    ]}
                />
            </Grid>
            {confirmModalState.showConfirmModal && (
                <LocalizedStrict id="sales-accounts-confirm-delete-dialog" attrs={{ typeOfEntity: true }}>
                    <DeleteConfirmDialog
                        typeOfEntity="sales accounts"
                        entityName={confirmModalState.confirmModalText}
                        handleClick={handleDeleteClick}
                        open={confirmModalState.showConfirmModal}
                        onClose={() => setConfirmModalState(defaultModalState)}
                    />
                </LocalizedStrict>
            )}
        </>
    )
}
